/**
 * Template Enhancer
 * Adds data-path attributes to template elements for visual editing
 */

/**
 * Enhance template with data-path attributes
 * This helps identify elements in the visual editor
 */
export function enhanceTemplateForVisualEdit(template) {
  if (!template) return template;

  let enhanced = template;

  // Add data-path to common patterns in the template
  
  // Headlines and titles (h1-h6)
  enhanced = enhanced.replace(
    /<(h[1-6])\s+([^>]*)>(\{[^}]+\})/g,
    (match, tag, attrs, content) => {
      const dataPath = extractDataPath(content);
      if (dataPath) {
        return `<${tag} ${attrs} data-path="${dataPath}" data-type="headline">` + content;
      }
      return match;
    }
  );

  // Paragraphs and text content
  enhanced = enhanced.replace(
    /<(p|span)\s+([^>]*)>(\{[^}]+\})/g,
    (match, tag, attrs, content) => {
      const dataPath = extractDataPath(content);
      if (dataPath) {
        return `<${tag} ${attrs} data-path="${dataPath}" data-type="text">` + content;
      }
      return match;
    }
  );

  // Images
  enhanced = enhanced.replace(
    /<img\s+([^>]*)src=\{([^}]+)\}/g,
    (match, attrs, src) => {
      const dataPath = src.trim();
      return `<img ${attrs}src={${src}} data-path="${dataPath}" data-type="image"`;
    }
  );

  // Map iterations with data-index
  enhanced = enhanced.replace(
    /\.map\(\(([^,]+),\s*(\w+)\)\s*=>\s*\(/g,
    (match, item, index) => {
      // Add data-index tracking
      return match;
    }
  );

  return enhanced;
}

/**
 * Extract data path from JSX expression
 */
function extractDataPath(expression) {
  if (!expression) return null;

  // Remove curly braces
  const cleaned = expression.replace(/[{}]/g, '').trim();

  // Common patterns like copy.headline, data.context.tenant.name, etc.
  const patterns = [
    /^(copy\.[a-zA-Z_][a-zA-Z0-9_\.]*)/,
    /^(context\.[a-zA-Z_][a-zA-Z0-9_\.]*)/,
    /^(data\.[a-zA-Z_][a-zA-Z0-9_\.]*)/,
  ];

  for (const pattern of patterns) {
    const match = cleaned.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Inject data attributes into rendered elements
 * This is a runtime enhancement for elements that don't have data-path in template
 */
export function injectDataAttributes(containerElement, data) {
  if (!containerElement) return;

  // Find all text elements and try to match them to data
  const textElements = containerElement.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, a');
  
  textElements.forEach(element => {
    // Skip if already has data-path
    if (element.getAttribute('data-path')) return;

    const text = element.textContent?.trim();
    if (!text) return;

    // Try to find matching data path
    const dataPath = findDataPathByContent(data, text);
    if (dataPath) {
      element.setAttribute('data-path', dataPath);
      element.setAttribute('data-type', getElementType(element));
    }
  });

  // Find all images
  const images = containerElement.querySelectorAll('img');
  images.forEach(element => {
    if (element.getAttribute('data-path')) return;

    const src = element.getAttribute('src');
    if (!src) return;

    const dataPath = findDataPathByContent(data, src);
    if (dataPath) {
      element.setAttribute('data-path', dataPath);
      element.setAttribute('data-type', 'image');
    }
  });
}

/**
 * Find data path by matching content
 */
function findDataPathByContent(data, content, currentPath = 'data') {
  if (!data || !content) return null;

  // Handle different data types
  if (typeof data === 'string') {
    if (data === content || data.includes(content)) {
      return currentPath;
    }
    return null;
  }

  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      const result = findDataPathByContent(data[i], content, `${currentPath}[${i}]`);
      if (result) return result;
    }
    return null;
  }

  if (typeof data === 'object') {
    for (const [key, value] of Object.entries(data)) {
      const result = findDataPathByContent(value, content, `${currentPath}.${key}`);
      if (result) return result;
    }
    return null;
  }

  return null;
}

/**
 * Get element type from tag name
 */
function getElementType(element) {
  const tagName = element.tagName.toLowerCase();
  
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
    return 'headline';
  }
  
  if (['p', 'span', 'a'].includes(tagName)) {
    return 'text';
  }
  
  if (tagName === 'img') {
    return 'image';
  }
  
  if (['div', 'section', 'article'].includes(tagName)) {
    return 'container';
  }
  
  return 'element';
}

/**
 * Create wrapper component with data attributes
 */
export function createEditableWrapper(Component, dataPath, dataType) {
  return function EditableComponent(props) {
    return (
      <div data-path={dataPath} data-type={dataType}>
        <Component {...props} />
      </div>
    );
  };
}

